/**
 * 📣 AffiliateMarketingPage - Marketing-Material & Downloads
 */

import React from 'react';
import { AffiliateLayout } from '@/components/affiliate/layout/AffiliateLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Download, 
  FileImage, 
  FileText, 
  Video, 
  Link2,
  Copy,
  Check,
  ExternalLink,
  QrCode
} from 'lucide-react';

// ============================================
// MOCK DATA
// ============================================

const MARKETING_ASSETS = {
  banners: [
    { id: 1, name: 'Leaderboard Banner (728x90)', format: 'PNG', size: '45KB' },
    { id: 2, name: 'Skyscraper Banner (160x600)', format: 'PNG', size: '62KB' },
    { id: 3, name: 'Medium Rectangle (300x250)', format: 'PNG', size: '38KB' },
    { id: 4, name: 'Mobile Banner (320x50)', format: 'PNG', size: '22KB' },
    { id: 5, name: 'Social Media Post (1080x1080)', format: 'PNG', size: '156KB' },
  ],
  documents: [
    { id: 1, name: 'Verkaufsargumente Übersicht', format: 'PDF', size: '890KB' },
    { id: 2, name: 'Provisionsmodell Erklärt', format: 'PDF', size: '420KB' },
    { id: 3, name: 'Restaurant Onboarding Guide', format: 'PDF', size: '1.2MB' },
    { id: 4, name: 'FAQ für Restaurants', format: 'PDF', size: '340KB' },
  ],
  templates: [
    { id: 1, name: 'E-Mail Vorlage: Erstansprache', format: 'HTML', size: '12KB' },
    { id: 2, name: 'E-Mail Vorlage: Follow-Up', format: 'HTML', size: '10KB' },
    { id: 3, name: 'Telefonskript', format: 'DOCX', size: '28KB' },
    { id: 4, name: 'Social Media Textvorlagen', format: 'TXT', size: '8KB' },
  ],
  videos: [
    { id: 1, name: 'SpeiseGuru Produktvideo', duration: '2:30', format: 'MP4' },
    { id: 2, name: 'Top-Partner Interview', duration: '5:15', format: 'MP4' },
    { id: 3, name: 'Sales Pitch Training', duration: '12:00', format: 'MP4' },
  ],
};

const REFERRAL_LINKS = {
  standard: 'speiseguru.de/ref/martink',
  landing: 'speiseguru.de/partner/martink',
  demo: 'speiseguru.de/demo?ref=martink',
};

// ============================================
// COMPONENTS
// ============================================

interface AssetCardProps {
  name: string;
  format: string;
  size?: string;
  duration?: string;
  icon: React.ReactNode;
}

const AssetCard: React.FC<AssetCardProps> = ({ name, format, size, duration, icon }) => (
  <Card className="hover:shadow-md transition-shadow overflow-hidden">
    <CardContent className="p-3 sm:p-4 flex items-center gap-3 sm:gap-4">
      <div className="p-2 sm:p-3 bg-gray-100 rounded-lg shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0 overflow-hidden">
        <p className="font-medium text-xs sm:text-sm text-gray-900 truncate">{name}</p>
        <p className="text-[10px] sm:text-xs text-gray-500 truncate">
          {format} {size && `• ${size}`} {duration && `• ${duration}`}
        </p>
      </div>
      <Button variant="outline" size="sm" className="shrink-0 h-9 w-9 sm:h-auto sm:w-auto p-2 sm:p-2">
        <Download className="w-4 h-4" />
      </Button>
    </CardContent>
  </Card>
);

const LinkCard: React.FC<{ label: string; url: string }> = ({ label, url }) => {
  const [copied, setCopied] = React.useState(false);
  
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`https://${url}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
    }
  };
  
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 sm:p-4 bg-gray-50 rounded-lg overflow-hidden">
      <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
        <Link2 className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] sm:text-xs text-gray-500 mb-0.5 sm:mb-1">{label}</p>
          <p className="text-xs sm:text-sm font-mono text-gray-900 break-all select-all leading-relaxed">https://{url}</p>
        </div>
      </div>
      <div className="flex gap-1 shrink-0 self-end sm:self-auto">
        <Button 
          variant="ghost" 
          size="sm" 
          title={copied ? 'Kopiert!' : 'Kopieren'}
          className={`h-8 sm:h-9 px-3 gap-1.5 text-xs ${copied ? 'text-emerald-600' : ''}`}
          onClick={handleCopy}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? 'Kopiert!' : 'Kopieren'}</span>
        </Button>
        <Button variant="ghost" size="sm" title="QR-Code" className="h-8 w-8 sm:h-9 sm:w-9 p-0 hidden sm:flex">
          <QrCode className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="sm" 
          title="Öffnen" 
          className="h-8 w-8 sm:h-9 sm:w-9 p-0 hidden sm:flex"
          onClick={() => window.open(`https://${url}`, '_blank', 'noopener,noreferrer')}
        >
          <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// MAIN PAGE
// ============================================

export const AffiliateMarketingPage: React.FC = () => {
  return (
    <AffiliateLayout
      pageTitle="Marketing"
      pageSubtitle="Werbematerial und Links für deine Akquise"
    >
      {/* Referral Links Section */}
      <Card className="mb-4 sm:mb-6 overflow-hidden">
        <CardHeader className="px-4 sm:px-6 py-3 sm:py-4">
          <CardTitle className="text-sm sm:text-base font-semibold">Deine Referral-Links</CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-6 pb-3 sm:pb-6 pt-0 space-y-2 sm:space-y-3">
          <LinkCard label="Standard Referral" url={REFERRAL_LINKS.standard} />
          <LinkCard label="Partner Landingpage" url={REFERRAL_LINKS.landing} />
          <LinkCard label="Demo-Anfrage" url={REFERRAL_LINKS.demo} />
        </CardContent>
      </Card>

      {/* Asset Categories */}
      <Tabs defaultValue="banners" className="space-y-3 sm:space-y-4">
        <TabsList className="bg-gray-100 w-full flex overflow-x-auto">
          <TabsTrigger value="banners" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <FileImage className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Banner</span>
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Dokumente</span>
          </TabsTrigger>
          <TabsTrigger value="templates" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Vorlagen</span>
          </TabsTrigger>
          <TabsTrigger value="videos" className="flex-1 min-w-0 text-xs sm:text-sm px-2 sm:px-3">
            <Video className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Videos</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="banners">
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-sm sm:text-base font-semibold">Werbebanner</CardTitle>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm h-8 sm:h-9 w-full sm:w-auto">
                  <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                  Alle herunterladen
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 sm:gap-3 px-3 sm:px-6 pb-3 sm:pb-6">
              {MARKETING_ASSETS.banners.map(asset => (
                <AssetCard 
                  key={asset.id}
                  name={asset.name}
                  format={asset.format}
                  size={asset.size}
                  icon={<FileImage className="w-5 h-5 text-purple-600" />}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-sm sm:text-base font-semibold">Verkaufsdokumente</CardTitle>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm h-8 sm:h-9 w-full sm:w-auto">
                  <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                  Alle herunterladen
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 sm:gap-3 px-3 sm:px-6 pb-3 sm:pb-6">
              {MARKETING_ASSETS.documents.map(asset => (
                <AssetCard 
                  key={asset.id}
                  name={asset.name}
                  format={asset.format}
                  size={asset.size}
                  icon={<FileText className="w-5 h-5 text-red-600" />}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="templates">
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <CardTitle className="text-sm sm:text-base font-semibold">Kommunikationsvorlagen</CardTitle>
                <Button variant="outline" size="sm" className="text-xs sm:text-sm h-8 sm:h-9 w-full sm:w-auto">
                  <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" />
                  Alle herunterladen
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-2 sm:gap-3 px-3 sm:px-6 pb-3 sm:pb-6">
              {MARKETING_ASSETS.templates.map(asset => (
                <AssetCard 
                  key={asset.id}
                  name={asset.name}
                  format={asset.format}
                  size={asset.size}
                  icon={<FileText className="w-5 h-5 text-blue-600" />}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="videos">
          <Card className="overflow-hidden">
            <CardHeader className="pb-2 sm:pb-3 px-4 sm:px-6">
              <CardTitle className="text-sm sm:text-base font-semibold">Schulungs- & Werbevideos</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-2 sm:gap-3 px-3 sm:px-6 pb-3 sm:pb-6">
              {MARKETING_ASSETS.videos.map(asset => (
                <AssetCard 
                  key={asset.id}
                  name={asset.name}
                  format={asset.format}
                  duration={asset.duration}
                  icon={<Video className="w-5 h-5 text-orange-600" />}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AffiliateLayout>
  );
};

export default AffiliateMarketingPage;
